import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { FormikProps } from "formik";
import { OC_UPDATE_PROPERTY } from "oc/graphql";
import { useOcSnackbar } from "oc/hooks";

type UseOcFormikHelperItem = {
  key: string;
  loading: boolean;
  resource?: string;
} & (
  | {
      type: "input";
    }
  | {
      type: "switch";
      returnType: "boolean" | "number";
    }
);

// TODO f 'key' shouldn't be just string, it should be a generic type.
interface OnCompleteType {
  updateProperty: {
    resource: string;
    validFrom: null | string;
    value: unknown;
    __typename: string;
    key: string;
  };
}
// TODO f it should be possible to just require 'formik', and return only the isFieldDirty function.
interface UseOcFormikHelperProps<T extends object> {
  valuesExtraData: Record<
    keyof FormikProps<T>["values"],
    UseOcFormikHelperItem
  >;
  defaultResource: string;
  formik: FormikProps<T>;
  onCompleted?: (data: OnCompleteType) => void;
}

// TODO f this is a hook, not a util function.
export const useOcFormikHelper = <FormikValues extends object>({
  formik,
  defaultResource,
  valuesExtraData,
  onCompleted,
}: UseOcFormikHelperProps<FormikValues>) => {
  const { t } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();
  const [updatePropertyMutation, { loading, error }] =
    useMutation<OnCompleteType>(OC_UPDATE_PROPERTY);

  const updateProperty = useCallback(
    async (options: any) => {
      try {
        const result = await updatePropertyMutation(options);
        if (result.data) {
          if (onCompleted) onCompleted(result.data);
          setOpenMessage({
            type: "success",
            message: t("OC_PROPERTY_SAVE_SUCCEEDED"),
          });
        }
        return result;
      } catch (err: any) {
        alert("error" + err.toString());
        setOpenMessage({
          type: "error",
          message: t("OC_PROPERTY_SAVE_FAILED"),
        });
        throw err;
      }
    },
    [updatePropertyMutation, onCompleted, setOpenMessage, t],
  );

  const loadingState =
    Object.keys(valuesExtraData).every((key) => {
      const formikKey = key as keyof typeof valuesExtraData;
      return valuesExtraData[formikKey].loading;
    }) || loading;

  const isFieldDirty = (fieldName: keyof typeof formik.values) =>
    formik.values[fieldName] !== formik.initialValues[fieldName];

  // Only update values which have changed.
  const updateProperties = () => {
    Object.keys(valuesExtraData).forEach((key) => {
      const formikValueKey = key as keyof typeof valuesExtraData;
      const item = valuesExtraData[formikValueKey];
      const resource = item.resource ?? defaultResource;
      if (isFieldDirty(formikValueKey)) {
        let updateValue: unknown = formik.values[formikValueKey];

        if (item.type === "switch") {
          if (item.returnType === "boolean")
            updateValue = `${formik.values[formikValueKey]}`;
          else updateValue = formik.values[formikValueKey] ? "1" : "0";
        }

        updateProperty({
          variables: {
            resource,
            key: item.key,
            value: updateValue,
          },
        });
      }
    });
  };

  return {
    loadingState,
    updateProperties,
    isFieldDirty,
    error,
  };
};
